import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineChat,
  HiOutlineShare,
  HiPencil,
  HiBeaker,
  HiPhone,
  HiClock,
  HiFilter,
  HiX,
  HiCheck,
  HiKey,
} from "react-icons/hi";
import { IconWrapper } from "./IconWrapper";
import api from "../../services/api";
import { AGENT_ENDPOINTS, LANGUAGE_ENDPOINTS } from "../../config/api";
import { LanguageMapping, validateAgentToken } from "../../services/api";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import LoadingState from "../Common/LoadingState";
import { estimateTokenCount, formatTokenCount } from "../../utils/tokenCount";

interface Agent {
  uuid: string;
  name: string;
  description: string;
  enabled: boolean;
  is_outbound: boolean;
  is_public?: boolean;
  test_lab_access?: boolean;
  phone_supported?: boolean;
  primary_language: string;
  supported_languages: string[];
  tags: string[];
  limitations: string[];
  access_token?: string;
  system_prompt?: string;
  system_prompt_token_count?: number;
}

// First, add the Window type declaration at the top if it doesn't exist already
declare global {
  interface Window {
    updateRemainingUses?: (uses: number | null) => void;
  }
}

const AgentSelector: React.FC = () => {
  const [copiedAgent, setCopiedAgent] = useState<string | null>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [tokenSuccess, setTokenSuccess] = useState<boolean>(false);
  const [tokenLoading, setTokenLoading] = useState<boolean>(false);
  // const [tokenAgent, setTokenAgent] = useState<AgentSummary | null>(null);
  const [filters, setFilters] = useState({
    tags: [] as string[],
    languages: [] as string[],
    isOutbound: null as boolean | null,
    isPublic: null as boolean | null,
    isPhoneCall: null as boolean | null,
  });
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem("userType") === "admin";
  const canOpenTestLab = isAdmin
    ? Boolean(localStorage.getItem("adminToken"))
    : Boolean(localStorage.getItem("token"));
  const inputRef = useRef<HTMLInputElement>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentsLoading, setAgentsLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [offset, setOffset] = useState<number>(0);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const didLoadRef = useRef<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);

  // Use React Query for data fetching with caching
  const { data: languagesData, isLoading: languagesLoading } = useQuery({
    queryKey: ["languages"],
    queryFn: async () => {
      const response = await api.get(LANGUAGE_ENDPOINTS.MAPPINGS);
      const mappings = new Map<string, string>();
      response.data.forEach((mapping: LanguageMapping) => {
        mappings.set(mapping.language_code, mapping.language);
      });
      return mappings;
    },
    // Refresh configuration
    staleTime: 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const PAGE_SIZE = 12;

  const fetchInitialAgents = useCallback(async (searchTerm: string) => {
    try {
      setAgentsLoading(true);
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", "0");
      if (searchTerm) params.set("search", searchTerm);
      const response = await api.get(`${AGENT_ENDPOINTS.LIST}?${params.toString()}`);

      // Store remaining uses in localStorage for the navbar (non-admin)
      if (
        localStorage.getItem("userType") !== "admin" &&
        response.data.remaining_uses !== undefined
      ) {
        localStorage.setItem("remainingUses", response.data.remaining_uses.toString());
        if (window.updateRemainingUses) {
          window.updateRemainingUses(response.data.remaining_uses);
        }
      }

      setAgents(response.data.data || []);
      const pagination = response.data.pagination || {};
      setHasMore(Boolean(pagination.has_more));
      const nextOffset = (pagination.offset ?? 0) + (pagination.limit ?? PAGE_SIZE);
      setOffset(nextOffset);
    } finally {
      setAgentsLoading(false);
      setHasLoadedOnce(true);
    }
  }, []);

  const loadMoreAgents = useCallback(async () => {
    if (agentsLoading || isFetchingMore || !hasMore) return;
    try {
      setIsFetchingMore(true);
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      if (debouncedSearch) params.set("search", debouncedSearch);
      const response = await api.get(`${AGENT_ENDPOINTS.LIST}?${params.toString()}`);
      setAgents((prev) => [...prev, ...(response.data.data || [])]);
      const pagination = response.data.pagination || {};
      setHasMore(Boolean(pagination.has_more));
      const increment = pagination.limit ?? PAGE_SIZE;
      setOffset((prev) => prev + increment);
    } finally {
      setIsFetchingMore(false);
    }
  }, [agentsLoading, isFetchingMore, hasMore, offset, debouncedSearch]);

  // Initial load
  useEffect(() => {
    if (didLoadRef.current) return;
    didLoadRef.current = true;
    fetchInitialAgents("");
  }, [fetchInitialAgents]);

  // Refetch on window focus
  useEffect(() => {
    const handleFocus = () => fetchInitialAgents(debouncedSearch);
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [fetchInitialAgents, debouncedSearch]);

  // Refetch on debounced search change
  useEffect(() => {
    fetchInitialAgents(debouncedSearch);
  }, [debouncedSearch, fetchInitialAgents]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreAgents();
        }
      },
      { root: null, rootMargin: "200px", threshold: 0 }
    );
    observer.observe(node);
    return () => {
      observer.unobserve(node);
      observer.disconnect();
    };
  }, [loadMoreAgents]);

  const loading = languagesLoading || agentsLoading;
  // Manual refetch handled via focus effect above

  // Get all unique tags and languages from agents
  const allTags = useMemo(() => {
    if (!agents) return [] as string[];
    const tags = new Set<string>();
    agents.forEach((agent) => {
      agent.tags?.forEach((tag) => tags.add(tag));
    });
    return Array.from(tags);
  }, [agents]);

  const allLanguages = useMemo(() => {
    if (!agents || !languagesData) return [] as string[];
    const languages = new Set<string>();
    agents.forEach((agent) => {
      languages.add(agent.primary_language);
      agent.supported_languages?.forEach((lang) => languages.add(lang));
    });
    return Array.from(languages);
  }, [agents, languagesData]);

  // Count active filters
  useEffect(() => {
    let count = 0;
    count += filters.tags.length;
    count += filters.languages.length;
    if (filters.isOutbound !== null) count++;
    if (filters.isPublic !== null) count++;
    if (filters.isPhoneCall !== null) count++;
    setActiveFiltersCount(count);
  }, [filters]);

  // Filter agents based on all filters
  const filteredAgents = useMemo(() => {
    if (!agents) return [] as Agent[];

    // If no filter is active, return all agents
    if (
      filters.tags.length === 0 &&
      filters.languages.length === 0 &&
      filters.isOutbound === null &&
      filters.isPublic === null &&
      filters.isPhoneCall === null &&
      !selectedTag
    ) {
      return agents;
    }

    return agents.filter((agent) => {
      // Filter by tag from top buttons
      if (selectedTag && !agent.tags?.includes(selectedTag)) {
        return false;
      }

      // Filter by tags from modal
      if (
        filters.tags.length > 0 &&
        !filters.tags.some((tag) => agent.tags?.includes(tag))
      ) {
        return false;
      }

      // Filter by languages
      if (
        filters.languages.length > 0 &&
        !filters.languages.some(
          (lang) =>
            agent.primary_language === lang ||
            agent.supported_languages?.includes(lang)
        )
      ) {
        return false;
      }

      // Filter by outbound status
      if (
        filters.isOutbound !== null &&
        agent.is_outbound !== filters.isOutbound
      ) {
        return false;
      }

      // Filter by public
      if (filters.isPublic !== null && agent.is_public !== filters.isPublic) {
        return false;
      }

      // Filter by phone call
      if (
        filters.isPhoneCall !== null &&
        agent.phone_supported !== filters.isPhoneCall
      ) {
        return false;
      }

      return true;
    });
  }, [agents, selectedTag, filters]);

  const selectTag = (tag: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedTag((prev) => (prev === tag ? null : tag));
  };

  const handleToggleFilter = () => {
    setShowFilterModal(!showFilterModal);
  };

  const handleResetFilters = () => {
    setFilters({
      tags: [],
      languages: [],
      isOutbound: null,
      isPublic: null,
      isPhoneCall: null,
    });
  };

  // const handleApplyFilters = () => {
  //   setShowFilterModal(false);
  // };

  const handleAgentSelect = (agent: Agent) => {
    navigate(`/agent/${agent.uuid}`);
  };

  const handleShareAgent = (
    e: React.MouseEvent,
    agentUuid: string,
    accessToken?: string
  ) => {
    e.stopPropagation();
    // Include access token in the link if available
    let link = `${window.location.origin}/access?agent=${agentUuid}`;
    if (accessToken) {
      link += `&token=${accessToken}`;
    }

    navigator.clipboard
      .writeText(link)
      .then(() => {
        setCopiedAgent(agentUuid);
        setTimeout(() => setCopiedAgent(null), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy link: ", err);
      });
  };

  const handleEditAgent = (e: React.MouseEvent, agentUuid: string) => {
    e.stopPropagation();
    navigate(
      isAdmin
        ? `/admin/agents/${agentUuid}`
        : `/agent-config/${agentUuid}`
    );
  };

  const getLanguageName = (code: string): string => {
    return languagesData?.get(code) || code;
  };

  const handleShowTokenModal = () => {
    setShowTokenModal(true);
    setAccessToken("");
    setTokenError(null);
    setTokenSuccess(false);
    // reset selected agent for token flow if needed

    // Focus the input field after modal is displayed
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);
  };

  const handleCloseTokenModal = () => {
    setShowTokenModal(false);
  };

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccessToken(e.target.value);
    if (tokenError) {
      setTokenError(null);
    }
  };

  const handleValidateToken = async () => {
    if (!accessToken.trim()) {
      setTokenError("Please enter an access token");
      return;
    }

    // Store token before validation to prevent changes during validation
    const currentToken = accessToken.trim();

    setTokenLoading(true);
    setTokenError(null);

    try {
      const result = await validateAgentToken(currentToken);

      if (result.success && result.agent_info) {
        // Update remaining uses in localStorage if available
        if (result.remainingUses !== undefined) {
          localStorage.setItem(
            "remainingUses",
            result.remainingUses.toString()
          );
          if (window.updateRemainingUses) {
            window.updateRemainingUses(result.remainingUses);
          }
        }

        // Refresh agents list to include the newly accessible agent
        fetchInitialAgents(debouncedSearch);

        // Navigate to agent immediately
        navigate(`/agent/${result.agent_info.uuid}`);
      } else {
        setTokenError(result.error || "Failed to validate token");
        setTokenLoading(false);
      }
    } catch (error: any) {
      setTokenError(error.message || "An unexpected error occurred");
      setTokenLoading(false);
    }
  };

  // Input component that maintains focus
  const TokenInput = React.memo(
    ({
      value,
      onChange,
      disabled,
      onEnterPress,
    }: {
      value: string;
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
      disabled: boolean;
      onEnterPress?: () => void;
    }) => {
      const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && onEnterPress) {
          e.preventDefault();
          onEnterPress();
        }
      };

      return (
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <HiKey className="w-5 h-5 text-[var(--foreground-muted)]" />
          </div>
          <input
            type="text"
            id="access-token"
            value={value}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            className="bg-[var(--muted)]/30 border border-[var(--border)] text-[var(--foreground)] text-sm rounded-lg focus:ring-[var(--accent)] focus:border-[var(--accent)] block w-full pl-10 p-3"
            placeholder="Enter access token"
            disabled={disabled}
            autoComplete="off"
            ref={inputRef}
          />
        </div>
      );
    }
  );

  // Token Validation Modal
  const TokenValidationModal = React.memo(() => {
    // Focus input field when opened
    useEffect(() => {
      if (showTokenModal && inputRef.current) {
        inputRef.current.focus();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showTokenModal]);

    if (!showTokenModal) return null;

    return (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleCloseTokenModal}
      >
        <div
          className="bg-[var(--card-container)] rounded-xl border border-[var(--border)] w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-[var(--foreground)]">
                Access Agent with Token
              </h3>
              <button
                onClick={handleCloseTokenModal}
                className="p-1.5 rounded-md hover:bg-[var(--muted)] text-[var(--foreground-muted)]"
              >
                <HiX size={20} />
              </button>
            </div>

            <p className="text-sm text-[var(--foreground-muted)] mb-4">
              Enter your access token to gain access to a specific agent.
            </p>

            <div className="mb-6">
              <TokenInput
                value={accessToken}
                onChange={handleTokenChange}
                disabled={tokenLoading || tokenSuccess}
                onEnterPress={
                  accessToken.trim() && !tokenLoading && !tokenSuccess
                    ? handleValidateToken
                    : undefined
                }
              />

              {tokenError && (
                <div className="text-red-500 text-sm mb-4 bg-red-100 dark:bg-red-900/20 p-3 rounded-md">
                  {tokenError}
                </div>
              )}

              {tokenLoading && (
                <div className="flex justify-center items-center py-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--accent)]"></div>
                  <span className="ml-2 text-sm text-[var(--foreground-muted)]">
                    Validating token...
                  </span>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleCloseTokenModal}
                className="px-4 py-2 mr-2 text-[var(--foreground-muted)] hover:text-[var(--foreground)] transition-colors"
                disabled={tokenLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleValidateToken}
                disabled={!accessToken.trim() || tokenLoading || tokenSuccess}
                className={`px-4 py-2 bg-[#10b981] text-white rounded-md transition-colors ${
                  !accessToken.trim() || tokenLoading || tokenSuccess
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-[#0d9668]"
                }`}
              >
                Validate Token
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  });

  // Filter Modal Component - Extract to a separate component with its own state
  const FilterModal = React.memo(() => {
    // Use local state for filters to prevent modal refreshes on parent state changes
    const [localFilters, setLocalFilters] = useState({ ...filters });

    useEffect(() => {
      // Only update local state when modal opens
      if (showFilterModal) {
        setLocalFilters({ ...filters });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showFilterModal]);

    const handleLocalToggleTag = (tag: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setLocalFilters((prev) => ({
        ...prev,
        tags: prev.tags.includes(tag)
          ? prev.tags.filter((t) => t !== tag)
          : [...prev.tags, tag],
      }));
    };

    const handleLocalToggleLanguage = (
      language: string,
      e: React.MouseEvent
    ) => {
      e.stopPropagation();
      setLocalFilters((prev) => ({
        ...prev,
        languages: prev.languages.includes(language)
          ? prev.languages.filter((l) => l !== language)
          : [...prev.languages, language],
      }));
    };

    const handleLocalToggleOutbound = (value: boolean, e: React.MouseEvent) => {
      e.stopPropagation();
      setLocalFilters((prev) => ({
        ...prev,
        isOutbound: prev.isOutbound === value ? null : value,
      }));
    };

    const handleLocalTogglePublic = (value: boolean, e: React.MouseEvent) => {
      e.stopPropagation();
      setLocalFilters((prev) => ({
        ...prev,
        isPublic: prev.isPublic === value ? null : value,
      }));
    };

    const handleLocalTogglePhoneCall = (
      value: boolean,
      e: React.MouseEvent
    ) => {
      e.stopPropagation();
      setLocalFilters((prev) => ({
        ...prev,
        isPhoneCall: prev.isPhoneCall === value ? null : value,
      }));
    };

    const handleLocalReset = (e: React.MouseEvent) => {
      e.stopPropagation();
      setLocalFilters({
        tags: [],
        languages: [],
        isOutbound: null,
        isPublic: null,
        isPhoneCall: null,
      });
    };

    const handleLocalApply = (e: React.MouseEvent) => {
      e.stopPropagation();
      // Apply local filters to parent state only when "Apply" is clicked
      setFilters(localFilters);
      setShowFilterModal(false);
    };

    const handleClose = (e: React.MouseEvent) => {
      e.stopPropagation();
      setShowFilterModal(false);
    };

    if (!showFilterModal) return null;

    return (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <div
          className="bg-[var(--card-container)] rounded-xl border border-[var(--border)] w-full max-w-md max-h-[80vh] overflow-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold">Filter Agents</h3>
              <button
                onClick={handleClose}
                className="p-1.5 rounded-md hover:bg-[var(--muted)] text-[var(--foreground-muted)]"
              >
                <HiX size={20} />
              </button>
            </div>

            {/* Tags Section */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold mb-3 text-[var(--foreground)]">
                Tags
              </h4>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={(e) => handleLocalToggleTag(tag, e)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 flex items-center gap-1.5 ${
                      localFilters.tags.includes(tag)
                        ? "bg-[var(--accent)] text-white"
                        : "bg-[var(--muted)] text-[var(--foreground-muted)] hover:bg-[var(--muted)]/80"
                    }`}
                  >
                    {localFilters.tags.includes(tag) && <HiCheck size={14} />}
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Languages Section */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold mb-3 text-[var(--foreground)]">
                Languages
              </h4>
              <div className="flex flex-wrap gap-2">
                {allLanguages.map((langCode) => (
                  <button
                    key={langCode}
                    onClick={(e) => handleLocalToggleLanguage(langCode, e)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 flex items-center gap-1.5 ${
                      localFilters.languages.includes(langCode)
                        ? "bg-[var(--accent)] text-white"
                        : "bg-[var(--muted)] text-[var(--foreground-muted)] hover:bg-[var(--muted)]/80"
                    }`}
                  >
                    {localFilters.languages.includes(langCode) && (
                      <HiCheck size={14} />
                    )}
                    {getLanguageName(langCode)}
                  </button>
                ))}
              </div>
            </div>

            {/* Type Section */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold mb-3 text-[var(--foreground)]">
                Call Type
              </h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={(e) => handleLocalToggleOutbound(true, e)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 flex items-center gap-1.5 ${
                    localFilters.isOutbound === true
                      ? "bg-[var(--accent)] text-white"
                      : "bg-[var(--muted)] text-[var(--foreground-muted)] hover:bg-[var(--muted)]/80"
                  }`}
                >
                  {localFilters.isOutbound === true && <HiCheck size={14} />}
                  Outbound
                </button>
                <button
                  onClick={(e) => handleLocalToggleOutbound(false, e)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 flex items-center gap-1.5 ${
                    localFilters.isOutbound === false
                      ? "bg-[var(--accent)] text-white"
                      : "bg-[var(--muted)] text-[var(--foreground-muted)] hover:bg-[var(--muted)]/80"
                  }`}
                >
                  {localFilters.isOutbound === false && <HiCheck size={14} />}
                  Inbound
                </button>
              </div>
            </div>

            {/* Public Section */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold mb-3 text-[var(--foreground)]">
                Public
              </h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={(e) => handleLocalTogglePublic(true, e)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 flex items-center gap-1.5 ${
                    localFilters.isPublic === true
                      ? "bg-[var(--accent)] text-white"
                      : "bg-[var(--muted)] text-[var(--foreground-muted)] hover:bg-[var(--muted)]/80"
                  }`}
                >
                  {localFilters.isPublic === true && <HiCheck size={14} />}
                  Public
                </button>
                <button
                  onClick={(e) => handleLocalTogglePublic(false, e)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 flex items-center gap-1.5 ${
                    localFilters.isPublic === false
                      ? "bg-[var(--accent)] text-white"
                      : "bg-[var(--muted)] text-[var(--foreground-muted)] hover:bg-[var(--muted)]/80"
                  }`}
                >
                  {localFilters.isPublic === false && <HiCheck size={14} />}
                  Private
                </button>
              </div>
            </div>

            {/* Phone Call Section */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold mb-3 text-[var(--foreground)]">
                Phone Call Support
              </h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={(e) => handleLocalTogglePhoneCall(true, e)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 flex items-center gap-1.5 ${
                    localFilters.isPhoneCall === true
                      ? "bg-[var(--accent)] text-white"
                      : "bg-[var(--muted)] text-[var(--foreground-muted)] hover:bg-[var(--muted)]/80"
                  }`}
                >
                  {localFilters.isPhoneCall === true && <HiCheck size={14} />}
                  Phone Call Enabled
                </button>
                <button
                  onClick={(e) => handleLocalTogglePhoneCall(false, e)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 flex items-center gap-1.5 ${
                    localFilters.isPhoneCall === false
                      ? "bg-[var(--accent)] text-white"
                      : "bg-[var(--muted)] text-[var(--foreground-muted)] hover:bg-[var(--muted)]/80"
                  }`}
                >
                  {localFilters.isPhoneCall === false && <HiCheck size={14} />}
                  Without Phone Call
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="pt-4 border-t border-[var(--border)] flex items-center justify-between">
              <button
                onClick={handleLocalReset}
                className="text-sm text-[var(--foreground-muted)] hover:text-[var(--foreground)] transition-colors"
              >
                Reset all
              </button>
              <button
                onClick={handleLocalApply}
                className="px-4 py-2 bg-[var(--accent)] text-white rounded-md hover:bg-[var(--accent-hover)] transition-colors"
              >
                Apply filters
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  });

  if (loading && !hasLoadedOnce) {
    return (
      <div className="container mx-auto py-8 px-4">
        <LoadingState message="Loading agents..." fullPage={true} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)] flex flex-col relative">
      {/* Filter Modal */}
      <FilterModal />

      {/* Token Validation Modal */}
      <TokenValidationModal />

      {/* Content Section */}
      <div className="flex-1 relative z-10">
        <div className="container-large py-4 md:py-8 px-2 sm:px-4">
          <div className="text-center w-full pt-2 sm:pt-4">
            {/* Tag filters and Filter button */}
            <div className="mb-4 sm:mb-6">
              <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 items-center">
                <button
                  onClick={() => setSelectedTag(null)}
                  className={`px-3 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold transition-all duration-200 ${
                    !selectedTag
                      ? "bg-[var(--color-accent)] text-white shadow-sm"
                      : "bg-[var(--muted)] text-[var(--foreground-muted)] hover:bg-[var(--muted)]/80"
                  }`}
                >
                  All
                </button>
                {allTags.slice(0, 5).map((tag) => (
                  <button
                    key={tag}
                    onClick={(e) => selectTag(tag, e)}
                    className={`px-3 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold transition-all duration-200 ${
                      selectedTag === tag
                        ? "bg-[var(--color-accent)] text-white shadow-sm"
                        : "bg-[var(--muted)] text-[var(--foreground-muted)] hover:bg-[var(--muted)]/80"
                    }`}
                  >
                    {tag}
                  </button>
                ))}

                {/* Filter button */}
                <motion.button
                  onClick={handleToggleFilter}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[var(--muted)] text-[var(--foreground-muted)] hover:bg-[var(--muted)]/80 flex items-center gap-1 sm:gap-2 ml-1 sm:ml-2 transition-all duration-200 relative"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <HiFilter size={14} className="sm:w-4 sm:h-4" />
                  <span className="text-xs sm:text-sm">Filters</span>
                  {activeFiltersCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[var(--accent)] text-white text-[10px] sm:text-xs flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                </motion.button>
              </div>
            </div>

            <motion.div
              className="rounded-xl p-4 sm:p-6 md:p-8 mb-8 mx-auto bg-[var(--card-container)] backdrop-blur-sm border border-[var(--border)] w-full max-w-5xl overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="text-xl md:text-2xl font-bold text-[var(--foreground)] mb-6 text-center">
                Select Your Voice Assistant
              </h2>

              {/* Access Token button */}
              <div className="flex justify-center mb-6">
                <motion.button
                  onClick={handleShowTokenModal}
                  className="px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg bg-[var(--accent)]/10 text-[var(--accent)] hover:bg-[var(--accent)]/20 border border-[var(--accent)]/30 flex items-center gap-2 transition-all duration-200 font-medium"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <HiKey size={18} />
                  <span>Add Agent</span>
                </motion.button>
              </div>

              {/* Search bar */}
              <div className="mb-6">
                <input
                  type="text"
                  placeholder="Search agents by name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full max-w-md mx-auto block px-3 py-2 border border-[var(--border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--accent)] bg-[var(--card)] text-[var(--foreground)]"
                />
              </div>

              {/* Display filtered results count */}
              {filteredAgents.length !== agents.length && (
                <motion.div
                  className="mb-4 text-sm text-[var(--foreground-muted)]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  Showing {filteredAgents.length} of {agents.length}{" "}
                  agents
                </motion.div>
              )}

              <div
                className={`grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6 ${
                  filteredAgents.length === 1 ? "justify-items-center" : ""
                }`}
              >
                <AnimatePresence initial={false} mode="popLayout">
                  {filteredAgents.length > 0 ? (
                    filteredAgents.map((agent) => {
                      const promptTokenCount =
                        agent.system_prompt_token_count ??
                        estimateTokenCount(agent.system_prompt || "");

                      return (
                      <motion.div
                        key={agent.uuid}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{
                          duration: 0.15,
                          exit: { duration: 0 },
                        }}
                        className={`bg-[var(--card)] rounded-lg border border-[var(--border)] hover:border-[var(--accent)]/30 transition-all duration-200 text-left relative touch-manipulation cursor-pointer group flex flex-col w-full overflow-hidden ${
                          filteredAgents.length === 1
                            ? "md:col-span-2 max-w-md"
                            : ""
                        }`}
                        onClick={() => handleAgentSelect(agent)}
                        whileHover={{
                          y: -5,
                          scale: 1.02,
                          transition: { duration: 0.1 },
                        }}
                        whileTap={{ scale: 0.99 }}
                        layout
                      >
                        <div className="p-4 sm:p-5 md:p-6 flex flex-col h-full">
                          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[var(--accent)]/20 flex items-center justify-center text-[var(--accent)] flex-shrink-0 group-hover:bg-[var(--accent)]/30 transition-colors border border-[var(--accent)]/20">
                              <IconWrapper icon={HiOutlineChat} size={16} />
                            </div>
                            <h3 className="text-sm sm:text-base md:text-lg font-medium text-[var(--foreground)] truncate max-w-[calc(100%-30px)]">
                              {agent.name}
                            </h3>
                          </div>

                          <div className="text-[var(--foreground-muted)] mb-2 sm:mb-3 flex-grow overflow-hidden text-left text-xs sm:text-sm">
                            <div className="line-clamp-2 sm:line-clamp-3">
                              {agent.description.split("\n").map((line, i) => (
                                <p key={i} className={i > 0 ? "mt-1" : ""}>
                                  {line}
                                </p>
                              ))}
                            </div>
                          </div>

                          {/* Tags */}
                          {agent.tags && agent.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 sm:gap-1.5 mb-2 sm:mb-3">
                              {agent.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="px-1.5 sm:px-2 py-0.5 bg-[var(--accent)]/10 text-[var(--accent)] text-[10px] sm:text-xs rounded-full border border-[var(--accent)]/20"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Agent Features */}
                          <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-3">
                            {agent.is_outbound ? (
                              <div className="flex items-center gap-1 text-[10px] sm:text-xs bg-[var(--accent)]/10 text-[var(--accent)] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border border-[var(--accent)]/20">
                                <HiPhone className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                <span>Outbound</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-[10px] sm:text-xs bg-[var(--accent)]/10 text-[var(--accent)] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border border-[var(--accent)]/20">
                                <HiPhone className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                <span>Inbound</span>
                              </div>
                            )}
                            {agent.is_public && (
                              <div className="flex items-center gap-1 text-[10px] sm:text-xs bg-[var(--accent)]/10 text-[var(--accent)] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border border-[var(--accent)]/20">
                                <HiClock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                <span>Public</span>
                              </div>
                            )}
                            {agent.phone_supported && (
                              <div className="flex items-center gap-1 text-[10px] sm:text-xs bg-[var(--accent)]/10 text-[var(--accent)] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border border-[var(--accent)]/20">
                                <HiPhone className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                <span>Phone Call</span>
                              </div>
                            )}
                          </div>

                          {/* Footer */}
                          <div className="flex items-center justify-between border-t border-[var(--border)] pt-2 sm:pt-3 mt-auto">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <div className="flex flex-col gap-0.5 min-w-0">
                                <div className="flex items-center gap-1 sm:gap-2">
                                  <span className="text-[10px] sm:text-xs text-[var(--foreground)]">
                                    {getLanguageName(agent.primary_language)}
                                  </span>
                                  {agent.supported_languages?.length > 1 && (
                                    <span className="text-[10px] sm:text-xs text-[var(--foreground-muted)]">
                                      +{agent.supported_languages.length - 1} more
                                    </span>
                                  )}
                                </div>
                                <span className="text-[10px] sm:text-xs text-[var(--foreground-muted)]">
                                  Prompt: {formatTokenCount(promptTokenCount)}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center">
                              <AnimatePresence>
                                {copiedAgent === agent.uuid && (
                                  <motion.span
                                    className="text-[10px] sm:text-xs text-[var(--accent)] mr-1 sm:mr-2"
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                  >
                                    Copied!
                                  </motion.span>
                                )}
                              </AnimatePresence>
                              <motion.button
                                onClick={(e) =>
                                  handleShareAgent(
                                    e,
                                    agent.uuid,
                                    agent.access_token
                                  )
                                }
                                className="p-1 sm:p-1.5 text-[var(--foreground-muted)] hover:text-[var(--accent)] hover:bg-[var(--muted)] rounded-md transition-colors ml-0.5 sm:ml-1"
                                aria-label="Share agent"
                                title="Share agent"
                                whileTap={{ scale: 0.95 }}
                              >
                                <IconWrapper icon={HiOutlineShare} size={14} />
                              </motion.button>
                              <motion.button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/agent/${agent.uuid}`);
                                }}
                                className="p-1 sm:p-1.5 text-[var(--foreground-muted)] hover:text-[var(--accent)] hover:bg-[var(--muted)] rounded-md transition-colors ml-0.5 sm:ml-1"
                                aria-label="Open Web Call"
                                title="Open Web Call"
                                whileTap={{ scale: 0.95 }}
                              >
                                <IconWrapper icon={HiPhone} size={14} />
                              </motion.button>
                              {canOpenTestLab && (isAdmin || agent.test_lab_access) && (
                                    <motion.button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/test-lab?agent=${agent.uuid}`);
                                      }}
                                      className="p-1 sm:p-1.5 text-[var(--foreground-muted)] hover:text-[var(--accent)] hover:bg-[var(--muted)] rounded-md transition-colors ml-0.5 sm:ml-1"
                                      aria-label="Open Test Lab"
                                      title="Open Test Lab"
                                      whileTap={{ scale: 0.95 }}
                                    >
                                      <IconWrapper icon={HiBeaker} size={14} />
                                    </motion.button>
                              )}
                              {(isAdmin || agent.test_lab_access) && (
                                <motion.button
                                  onClick={(e) =>
                                    handleEditAgent(e, agent.uuid)
                                  }
                                  className="p-1 sm:p-1.5 text-[var(--foreground-muted)] hover:text-[var(--accent)] hover:bg-[var(--muted)] rounded-md transition-colors ml-0.5 sm:ml-1"
                                  aria-label="Edit agent"
                                  title="Edit agent configuration"
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <IconWrapper icon={HiPencil} size={14} />
                                </motion.button>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                      );
                    })
                  ) : (
                    <motion.div
                      className="col-span-2 py-12 text-center text-[var(--foreground-muted)]"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="flex flex-col items-center justify-center">
                        <img
                          src="/assets/no-agents.png"
                          alt="No agents found"
                          className="w-48 h-auto mb-6"
                        />
                        <h3 className="text-2xl font-bold text-[var(--foreground)] mb-4">
                          No Results Found
                        </h3>
                        <p className="text-[var(--foreground-muted)] text-lg max-w-lg mx-auto mb-8">
                          We couldn't find any voice agents matching your
                          search. Try adjusting your filters or keywords.
                        </p>
                        <button
                          onClick={handleResetFilters}
                          className="px-6 py-3 bg-[var(--accent)] text-white rounded-md hover:bg-[var(--accent-hover)] transition-colors font-semibold"
                        >
                          Browse All Agents
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              {/* Infinite scroll sentinel and loader */}
              <div ref={sentinelRef} className="h-4" />
              {isFetchingMore && (
                <div className="flex justify-center items-center py-4">
                  <LoadingState message="Loading more agents..." fullPage={false} />
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentSelector;
